import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Play, Pause, Stop, UploadSimple, ArrowLeft, Trash, PencilSimple, Check, Clock, CalendarBlank, PaperPlaneTilt,
  WarningCircle, CheckCircle, DownloadSimple, BellRinging,
} from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import Layout from "@/components/Layout";
import { StatusBadge } from "@/components/StatusBadge";

const DAYS = [["Mon", 0], ["Tue", 1], ["Wed", 2], ["Thu", 3], ["Fri", 4], ["Sat", 5], ["Sun", 6]];
const WAIT_UNITS = [["Seconds", "seconds"], ["Minutes", "minutes"], ["Hours", "hours"], ["Days", "days"]];

function normalizeSeqState(seq) {
  const out = {};
  for (let m = 2; m <= 5; m++) {
    const c = (seq && seq[String(m)]) || {};
    out[String(m)] = {
      enabled: !!c.enabled,
      wait_value: c.wait_value || 30,
      wait_unit: c.wait_unit || "minutes",
    };
  }
  return out;
}

function fmtSched(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

function Metric({ label, value, color, testid }) {
  return (
    <div className="border border-sv-border bg-sv-surface p-4">
      <p className="font-mono text-[11px] text-sv-muted uppercase tracking-wider">{label}</p>
      <p className="font-mono font-medium text-3xl mt-1" style={{ color: color || "#fff" }} data-testid={testid}>
        {value}
      </p>
    </div>
  );
}

export default function CampaignDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [drag, setDrag] = useState(false);
  const [editMpm, setEditMpm] = useState(false);
  const [mpmVal, setMpmVal] = useState(10);
  const [win, setWin] = useState({ window_start: "", window_end: "", active_days: [0, 1, 2, 3, 4, 5, 6] });
  const [savingWin, setSavingWin] = useState(false);
  const [seq, setSeq] = useState(normalizeSeqState(null));
  const [savingSeq, setSavingSeq] = useState(false);
  const seqInit = useRef(false);
  const [notif, setNotif] = useState({ notification_email: "", notification_phone: "" });
  const [savingNotif, setSavingNotif] = useState(false);
  const notifInit = useRef(false);
  const [editSender, setEditSender] = useState(false);
  const [senderVal, setSenderVal] = useState("");
  const [testPhone, setTestPhone] = useState("");
  const [testMsg, setTestMsg] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [numbers, setNumbers] = useState([]);
  const [numbersError, setNumbersError] = useState(null);
  const [customFrom, setCustomFrom] = useState(false);
  const [lastImport, setLastImport] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [dupData, setDupData] = useState(null);
  const [dupReview, setDupReview] = useState(false);
  const [reviewIdx, setReviewIdx] = useState(0);
  const [reviewSkip, setReviewSkip] = useState(new Set());
  const [dupBusy, setDupBusy] = useState(false);
  const [skippedCount, setSkippedCount] = useState(0);
  const winInit = useRef(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [c, ct] = await Promise.all([
        api.get(`/campaigns/${id}`),
        api.get(`/campaigns/${id}/contacts`),
      ]);
      setCampaign(c.data);
      setContacts(ct.data);
      api.get("/settings").then(({ data }) => setSettings(data)).catch(() => {});
      if (!editMpm) setMpmVal(c.data.mpm);
      if (!editSender) setSenderVal(c.data.sender_id || "");
      if (!winInit.current) {
        setWin({
          window_start: c.data.window_start || "",
          window_end: c.data.window_end || "",
          active_days: c.data.active_days || [0, 1, 2, 3, 4, 5, 6],
        });
        winInit.current = true;
      }
      if (!seqInit.current) {
        setSeq(normalizeSeqState(c.data.sequence));
        seqInit.current = true;
      }
      if (!notifInit.current) {
        setNotif({
          notification_email: c.data.notification_email || "",
          notification_phone: c.data.notification_phone || "",
        });
        notifInit.current = true;
      }
    } catch (e) {
      if (e.response?.status === 404) {
        toast.error("Campaign not found");
        navigate("/dashboard");
      }
    }
  }, [id, navigate, editMpm, editSender]);

  const loadSkipped = useCallback(async () => {
    try {
      const { data } = await api.get(`/campaigns/${id}/skipped/count`);
      setSkippedCount(data.count);
    } catch (e) { /* ignore */ }
  }, [id]);

  useEffect(() => {
    load();
    loadSkipped();
    const t = setInterval(load, 2000);
    return () => clearInterval(t);
  }, [load, loadSkipped]);

  useEffect(() => {
    const fetchNumbers = () =>
      api
        .get("/sendivo/numbers")
        .then(({ data }) => {
          setNumbers(data.numbers || []);
          setNumbersError(data.error || null);
        })
        .catch(() => {});
    fetchNumbers();
    // refresh when the user comes back to the tab so newly-added Sendivo numbers appear
    window.addEventListener("focus", fetchNumbers);
    return () => window.removeEventListener("focus", fetchNumbers);
  }, []);

  useEffect(() => {
    if (!editSender) {
      const inList = numbers.some((n) => n.phone_number === (campaign?.sender_id || ""));
      setCustomFrom(!!(campaign?.sender_id && !inList));
    }
  }, [numbers, campaign, editSender]);

  const control = async (action) => {
    try {
      const { data } = await api.post(`/campaigns/${id}/${action}`);
      setCampaign(data);
      toast.success(`Campaign ${action === "start" ? "started" : action + "ed"}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".csv")) {
      toast.error("Please upload a .csv file");
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/campaigns/${id}/contacts/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setLastImport(data);
      toast.success(
        `${data.added} added · ${data.skipped_duplicates} dupes · ${data.blank} blank · ${data.invalid} invalid`
      );
      load();
      // Cross-reference the uploaded numbers against historical sends (all campaigns)
      try {
        const { data: dd } = await api.get(`/campaigns/${id}/upload-duplicates`);
        if (dd.duplicate_count > 0) {
          setReviewSkip(new Set());
          setReviewIdx(0);
          setDupReview(false);
          setDupData(dd);
        }
      } catch (e) { /* non-blocking */ }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const applySkip = async (ids) => {
    setDupBusy(true);
    try {
      if (ids.length) {
        const { data } = await api.post(`/campaigns/${id}/skip-duplicates`, { ids });
        toast.success(`${data.skipped} duplicate${data.skipped === 1 ? "" : "s"} skipped`);
      } else {
        toast.success("Kept all numbers");
      }
      setDupData(null);
      setDupReview(false);
      load();
      loadSkipped();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setDupBusy(false);
    }
  };

  const downloadSkipped = async () => {
    try {
      const res = await api.get(`/campaigns/${id}/skipped/export`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `skipped-${id}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Download failed");
    }
  };

  const saveMpm = async () => {
    try {
      const { data } = await api.patch(`/campaigns/${id}`, { mpm: Number(mpmVal) });
      setCampaign(data);
      setEditMpm(false);
      toast.success("Rate limit updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const toggleDay = (d) =>
    setWin((w) => ({
      ...w,
      active_days: w.active_days.includes(d)
        ? w.active_days.filter((x) => x !== d)
        : [...w.active_days, d].sort((a, b) => a - b),
    }));

  const saveWindow = async () => {
    setSavingWin(true);
    try {
      const { data } = await api.patch(`/campaigns/${id}`, {
        window_start: win.window_start || "",
        window_end: win.window_end || "",
        active_days: win.active_days,
      });
      setCampaign(data);
      toast.success("Send window updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSavingWin(false);
    }
  };

  const setStep = (m, patch) => setSeq((s) => ({ ...s, [m]: { ...s[m], ...patch } }));

  const saveSequence = async () => {
    setSavingSeq(true);
    try {
      const { data } = await api.patch(`/campaigns/${id}`, { sequence: seq });
      setCampaign(data);
      setSeq(normalizeSeqState(data.sequence));
      toast.success("Message sequence updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSavingSeq(false);
    }
  };

  const saveSender = async () => {
    try {
      const { data } = await api.patch(`/campaigns/${id}`, { sender_id: senderVal });
      setCampaign(data);
      setEditSender(false);
      toast.success("From number updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const saveNotif = async () => {
    setSavingNotif(true);
    try {
      const { data } = await api.patch(`/campaigns/${id}`, {
        notification_email: notif.notification_email.trim(),
        notification_phone: notif.notification_phone.trim(),
      });
      setCampaign(data);
      toast.success("Notification targets updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSavingNotif(false);
    }
  };

  const toggleOne = (cid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(cid) ? next.delete(cid) : next.add(cid);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected((prev) =>
      prev.size === contacts.length && contacts.length > 0 ? new Set() : new Set(contacts.map((c) => c.id))
    );
  };

  const deleteSelected = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    if (!window.confirm(`Remove ${ids.length} selected contact${ids.length === 1 ? "" : "s"}?`)) return;
    try {
      const { data } = await api.post(`/campaigns/${id}/contacts/delete`, { ids });
      toast.success(`Removed ${data.removed} contact${data.removed === 1 ? "" : "s"}`);
      setSelected(new Set());
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get(`/campaigns/${id}/export`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(campaign?.name || "campaign").replace(/[^a-z0-9-_]/gi, "_")}_results.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Results exported");
    } catch (err) {
      toast.error("Export failed");
    }
  };

  const removeInvalid = async () => {
    try {
      const { data } = await api.delete(`/campaigns/${id}/contacts/invalid`);
      toast.success(`Removed ${data.removed} invalid contact${data.removed === 1 ? "" : "s"}`);
      setLastImport((prev) => (prev ? { ...prev, invalid: 0, invalid_samples: [] } : prev));
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const sendTest = async () => {
    if (!testPhone.trim()) {
      toast.error("Enter a phone number to test");
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post(`/campaigns/${id}/test-send`, {
        phone: testPhone.trim(),
        message: testMsg.trim() || undefined,
      });
      setTestResult({ ok: true, ...data });
      toast.success(`Test ${data.via === "mock" ? "(mock) " : ""}sent → ${data.message_id}`);
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || err.message;
      setTestResult({ ok: false, error: msg });
      toast.error(msg);
    } finally {
      setTesting(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this campaign and all its contacts?")) return;
    try {
      await api.delete(`/campaigns/${id}`);
      toast.success("Campaign deleted");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  if (!campaign) {
    return (
      <Layout>
        <p className="font-mono text-sm text-sv-muted">LOADING…</p>
      </Layout>
    );
  }

  const s = campaign.stats || {};
  const done = (s.sent || 0) + (s.failed || 0);
  const pct = s.total ? Math.round((done / s.total) * 100) : 0;
  const isRunning = campaign.status === "running";
  const seqCount = 1 + [2, 3, 4, 5].filter((m) => campaign.sequence?.[String(m)]?.enabled).length;
  const effectiveFrom = campaign.sender_id || settings?.sender_id || "";
  const fromInfo = numbers.find((n) => n.phone_number === effectiveFrom);
  const fromInactive = !!(settings && !settings.use_mock && fromInfo && !fromInfo.active);
  const canStart = !isRunning && (s.pending || 0) > 0 && !fromInactive;

  return (
    <Layout>
      <button
        onClick={() => navigate("/dashboard")}
        data-testid="back-button"
        className="flex items-center gap-2 font-mono text-xs text-sv-muted hover:text-sv-ink transition-colors duration-150"
      >
        <ArrowLeft size={14} weight="bold" /> ALL_CAMPAIGNS
      </button>

      {/* Header + Command Bar */}
      <div className="sticky top-14 z-20 -mx-6 px-6 py-4 mt-4 bg-sv-bg/95 backdrop-blur border-b border-sv-border">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <h1 className="font-display font-black text-3xl tracking-tight uppercase" data-testid="campaign-title">
              {campaign.name}
            </h1>
            <StatusBadge status={campaign.status} />
            {campaign.status === "running" && campaign.in_window === false && (
              <span
                data-testid="outside-window-badge"
                className="inline-flex items-center gap-1.5 px-2 py-1 border font-mono text-[11px] uppercase tracking-wider"
                style={{ color: "#B45309", borderColor: "#B4530955", background: "#B4530912" }}
              >
                <Clock size={12} weight="bold" /> Waiting · window
              </span>
            )}
          </div>
          <div className="flex items-center gap-2" data-testid="command-bar">
            <button
              data-testid="btn-start-campaign"
              onClick={() => control("start")}
              disabled={!canStart}
              title={fromInactive ? "From number is inactive — pick an active number to start" : undefined}
              className="flex items-center gap-2 h-10 px-5 bg-sv-live text-black font-semibold text-sm hover:brightness-110 transition-all duration-150 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <Play size={16} weight="fill" /> {campaign.status === "paused" || campaign.status === "stopped" ? "Resume" : "Start"}
            </button>
            <button
              data-testid="btn-pause-campaign"
              onClick={() => control("pause")}
              disabled={!isRunning}
              className="flex items-center gap-2 h-10 px-5 border border-sv-paused text-sv-paused font-semibold text-sm hover:bg-sv-paused/10 transition-colors duration-150 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <Pause size={16} weight="fill" /> Pause
            </button>
            <button
              data-testid="btn-stop-campaign"
              onClick={() => control("stop")}
              disabled={campaign.status === "stopped" || campaign.status === "draft" || campaign.status === "completed"}
              className="flex items-center gap-2 h-10 px-5 border border-sv-stop text-sv-stop font-semibold text-sm hover:bg-sv-stop/10 transition-colors duration-150 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <Stop size={16} weight="fill" /> Stop
            </button>
            <button
              data-testid="btn-delete-campaign"
              onClick={remove}
              className="flex items-center justify-center h-10 w-10 border border-sv-border text-sv-muted hover:text-sv-stop hover:border-sv-stop transition-colors duration-150"
            >
              <Trash size={16} weight="bold" />
            </button>
          </div>
        </div>
      </div>

      {/* Sending mode banner */}
      {settings && (
        <div
          data-testid="mode-banner"
          className="mt-4 border px-4 py-3 flex items-start gap-3"
          style={
            settings.use_mock
              ? { color: "#B45309", borderColor: "#B4530955", background: "#B4530912" }
              : { color: "#16A34A", borderColor: "#16A34A55", background: "#16A34A12" }
          }
        >
          {settings.use_mock ? (
            <WarningCircle size={20} weight="fill" className="mt-0.5 shrink-0" />
          ) : (
            <CheckCircle size={20} weight="fill" className="mt-0.5 shrink-0" />
          )}
          <div className="font-mono text-xs leading-relaxed">
            {settings.use_mock ? (
              <>
                <span className="font-bold">MOCK MODE</span> — messages are <span className="font-bold">SIMULATED</span> and
                NOT actually sent. Rows will show “SENT · MOCK”. Switch to Live in{" "}
                <button onClick={() => navigate("/manage")} className="underline">Manage</button> to send real texts.
              </>
            ) : (
              <>
                <span className="font-bold">LIVE MODE</span> — real SMS are sent from{" "}
                <span className="font-bold">{campaign.sender_id || settings.sender_id || "no from-number set"}</span>.
              </>
            )}
          </div>
        </div>
      )}

      {/* Inactive From-number warning */}
      {fromInactive && (
        <div
          data-testid="inactive-from-warning"
          className="mt-3 border px-4 py-3 flex items-start gap-3"
          style={{ color: "#DC2626", borderColor: "#DC262655", background: "#DC262612" }}
        >
          <WarningCircle size={20} weight="fill" className="mt-0.5 shrink-0" />
          <div className="font-mono text-xs leading-relaxed">
            <span className="font-bold">FROM NUMBER INACTIVE</span> — {effectiveFrom} is not active, so
            sending is <span className="font-bold">blocked</span>. Pick an active number in the “From number” field
            below before starting.
          </div>
        </div>
      )}

      {/* Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mt-6">
        <Metric label="Total" value={s.total || 0} testid="metric-total" />
        <Metric label="Sent" value={s.sent || 0} color="#16A34A" testid="metric-sent" />
        <Metric label="Pending" value={(s.pending || 0) + (s.sending || 0) + (s.scheduled || 0)} color="#B45309" testid="metric-pending" />
        <Metric label="Failed" value={s.failed || 0} color="#DC2626" testid="metric-failed" />
        <div className="border border-sv-border bg-sv-surface p-4">
          <p className="font-mono text-[11px] text-sv-muted uppercase tracking-wider flex items-center gap-2">
            Rate limit
            {!editMpm && (
              <button data-testid="edit-mpm-button" onClick={() => setEditMpm(true)} className="text-sv-secondary hover:text-sv-ink">
                <PencilSimple size={13} weight="bold" />
              </button>
            )}
          </p>
          {editMpm ? (
            <div className="flex items-center gap-2 mt-1">
              <input
                data-testid="mpm-edit-input"
                type="number"
                min="1"
                max="600"
                value={mpmVal}
                onChange={(e) => setMpmVal(e.target.value)}
                className="w-20 h-9 bg-sv-bg border border-sv-border px-2 text-lg font-mono focus:outline-none focus:ring-1 focus:ring-sv-ink"
              />
              <button data-testid="save-mpm-button" onClick={saveMpm} className="h-9 w-9 flex items-center justify-center bg-sv-live text-black">
                <Check size={16} weight="bold" />
              </button>
            </div>
          ) : (
            <p className="font-mono font-medium text-3xl mt-1" data-testid="metric-mpm">
              {campaign.mpm}
              <span className="text-sm text-sv-muted">/min</span>
            </p>
          )}
        </div>
      </div>

      {/* Progress */}
      <div className="mt-4 border border-sv-border bg-sv-surface p-4">
        <div className="flex items-center justify-between font-mono text-xs text-sv-secondary mb-2">
          <span>PROGRESS</span>
          <span data-testid="progress-percent">{pct}% · {done}/{s.total || 0}</span>
        </div>
        <div className="flex gap-0.5 h-3">
          {Array.from({ length: 40 }).map((_, i) => {
            const filled = i < Math.round((pct / 100) * 40);
            return (
              <div
                key={i}
                className="flex-1 transition-colors duration-300"
                style={{ background: filled ? "#16A34A" : "#E4E4E7", border: "1px solid #D4D4D8" }}
              />
            );
          })}
        </div>
      </div>

      {/* Sender / From number (per-campaign) */}
      <div className="mt-8">
        <p className="font-mono text-xs text-sv-muted uppercase tracking-widest mb-3">// sender · from number</p>
        <div className="border border-sv-border bg-sv-surface p-5 flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[240px]">
            <label className="font-mono text-xs text-sv-secondary uppercase flex items-center gap-1.5">
              <PaperPlaneTilt size={13} weight="bold" /> From number / Sender ID
              {fromInfo && (
                <span
                  data-testid="from-status-pill"
                  className="ml-1 px-1.5 py-0.5 border text-[10px] uppercase tracking-wider"
                  style={
                    fromInfo.active
                      ? { color: "#16A34A", borderColor: "#16A34A55", background: "#16A34A12" }
                      : { color: "#DC2626", borderColor: "#DC262655", background: "#DC262612" }
                  }
                >
                  {fromInfo.active ? "active" : "inactive"}
                  {fromInfo.is_default ? " · default" : ""}
                </span>
              )}
            </label>
            <select
              data-testid="campaign-sender-select"
              value={customFrom ? "__custom__" : senderVal || ""}
              onChange={(e) => {
                const v = e.target.value;
                setEditSender(true);
                if (v === "__custom__") {
                  setCustomFrom(true);
                } else {
                  setCustomFrom(false);
                  setSenderVal(v);
                }
              }}
              className="mt-1 w-full h-11 bg-sv-bg border border-sv-border px-3 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-sv-ink focus:border-sv-ink transition-colors duration-150"
            >
              <option value="">— Use Manage default —</option>
              {numbers.map((n) => (
                <option key={n.phone_number} value={n.phone_number}>
                  {n.phone_number}
                  {n.friendly_name ? ` (${n.friendly_name})` : ""}
                  {n.is_default ? " · default" : ""}
                  {!n.active ? " · inactive" : ""}
                </option>
              ))}
              <option value="__custom__">Custom / type manually…</option>
            </select>
            {customFrom && (
              <input
                data-testid="campaign-sender-input"
                value={senderVal}
                onChange={(e) => {
                  setEditSender(true);
                  setSenderVal(e.target.value);
                }}
                placeholder="e.g. +19285435493 or BRAND"
                className="mt-2 w-full h-11 bg-sv-bg border border-sv-border px-3 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-sv-ink focus:border-sv-ink transition-colors duration-150"
              />
            )}
            <p className="mt-2 text-xs text-sv-muted">
              {numbersError
                ? `Couldn't load your numbers (${numbersError}). Choose “Custom” to type one.`
                : numbers.length === 0
                ? "No numbers found in your account yet — add one, or choose “Custom” to type a sender."
                : "Pick one of your numbers — add more and they'll show up here. Empty = Manage default."}
            </p>
          </div>
          <button
            data-testid="save-sender-button"
            onClick={saveSender}
            className="flex items-center gap-2 h-11 px-5 bg-sv-ink text-sv-onink font-semibold text-sm hover:bg-sv-secondary transition-colors duration-150"
          >
            <Check size={16} weight="bold" /> Save from number
          </button>
        </div>
      </div>

      {/* Notification targets (positive-lead alerts) */}
      <div className="mt-8">
        <p className="font-mono text-xs text-sv-muted uppercase tracking-widest mb-3">
          // alerts · positive-lead notifications
        </p>
        <div className="border border-sv-border bg-sv-surface p-5 space-y-4" data-testid="notification-config">
          <p className="font-mono text-[11px] text-sv-muted leading-relaxed flex items-start gap-2">
            <BellRinging size={14} weight="bold" className="mt-0.5 shrink-0" />
            When a lead replies and AI classifies it as <span className="text-sv-live font-bold">Positive</span> (≥80%
            confidence), you get an instant SMS + email with their reply and a link to the conversation.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="font-mono text-xs text-sv-secondary uppercase">Notification email</label>
              <input
                data-testid="notify-email-input"
                type="email"
                value={notif.notification_email}
                onChange={(e) => setNotif({ ...notif, notification_email: e.target.value })}
                placeholder="you@company.com"
                className="mt-1 w-full h-11 bg-sv-bg border border-sv-border px-3 text-sm focus:outline-none focus:ring-1 focus:ring-sv-ink focus:border-sv-ink transition-colors duration-150"
              />
            </div>
            <div>
              <label className="font-mono text-xs text-sv-secondary uppercase">Notification phone (SMS)</label>
              <input
                data-testid="notify-phone-input"
                value={notif.notification_phone}
                onChange={(e) => setNotif({ ...notif, notification_phone: e.target.value })}
                placeholder="+19285435493"
                className="mt-1 w-full h-11 bg-sv-bg border border-sv-border px-3 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-sv-ink focus:border-sv-ink transition-colors duration-150"
              />
            </div>
          </div>
          <button
            data-testid="save-notif-button"
            onClick={saveNotif}
            disabled={savingNotif}
            className="flex items-center gap-2 h-10 px-5 bg-sv-ink text-sv-onink font-semibold text-sm hover:bg-sv-secondary transition-colors duration-150 disabled:opacity-50"
          >
            <Check size={16} weight="bold" /> {savingNotif ? "Saving…" : "Save notification targets"}
          </button>
        </div>
      </div>

      {/* Send Test SMS */}
      <div className="mt-8">
        <p className="font-mono text-xs text-sv-muted uppercase tracking-widest mb-3">
          // send test · check a number before running the list
        </p>
        <div className="border border-sv-border bg-sv-surface p-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-[minmax(200px,280px)_1fr_auto] gap-4 items-end">
            <div>
              <label className="font-mono text-xs text-sv-secondary uppercase">Test phone (to)</label>
              <input
                data-testid="test-phone-input"
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value)}
                placeholder="+19285435493"
                className="mt-1 w-full h-11 bg-sv-bg border border-sv-border px-3 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-sv-ink focus:border-sv-ink transition-colors duration-150"
              />
            </div>
            <div>
              <label className="font-mono text-xs text-sv-secondary uppercase">Message (optional)</label>
              <input
                data-testid="test-message-input"
                value={testMsg}
                onChange={(e) => setTestMsg(e.target.value)}
                placeholder="Defaults to a test message"
                className="mt-1 w-full h-11 bg-sv-bg border border-sv-border px-3 text-sm focus:outline-none focus:ring-1 focus:ring-sv-ink focus:border-sv-ink transition-colors duration-150"
              />
            </div>
            <button
              data-testid="send-test-button"
              onClick={sendTest}
              disabled={testing}
              className="h-11 px-5 bg-sv-ink text-sv-onink font-semibold text-sm flex items-center gap-2 hover:bg-sv-secondary transition-colors duration-150 disabled:opacity-50"
            >
              <PaperPlaneTilt size={16} weight="fill" />
              {testing ? "Sending…" : "Send test"}
            </button>
          </div>
          <p className="font-mono text-[11px] text-sv-muted">
            Sends a single one-off SMS from this campaign’s From number. It is NOT added to the list and does not affect
            stats. Uses your current mode (LIVE or MOCK).
          </p>
          {testResult && (
            <div
              data-testid="test-send-result"
              className="border px-4 py-3 font-mono text-xs"
              style={
                testResult.ok
                  ? { color: "#16A34A", borderColor: "#16A34A55", background: "#16A34A12" }
                  : { color: "#DC2626", borderColor: "#DC262655", background: "#DC262612" }
              }
            >
              {testResult.ok ? (
                <>
                  ✓ SENT {testResult.via === "mock" ? "(MOCK)" : "(LIVE)"} · from {testResult.from} → {testResult.to} ·
                  id {testResult.message_id}
                </>
              ) : (
                <>✕ {testResult.error}</>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Send Window */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-3">
          <p className="font-mono text-xs text-sv-muted uppercase tracking-widest">
            // send window · arizona time (mst)
          </p>
          <span
            data-testid="window-status"
            className="font-mono text-[11px] uppercase tracking-wider"
            style={{ color: campaign.in_window ? "#16A34A" : "#B45309" }}
          >
            {campaign.in_window ? "● WITHIN WINDOW" : "● OUTSIDE WINDOW"}
          </span>
        </div>
        <div className="border border-sv-border bg-sv-surface p-5 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-[auto_auto_1fr] gap-4 items-end">
            <div>
              <label className="font-mono text-xs text-sv-secondary uppercase flex items-center gap-1.5">
                <Clock size={13} weight="bold" /> Start
              </label>
              <input
                data-testid="window-start-input"
                type="time"
                value={win.window_start}
                onChange={(e) => setWin({ ...win, window_start: e.target.value })}
                className="mt-1 h-11 bg-sv-bg border border-sv-border px-3 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-sv-ink focus:border-sv-ink transition-colors duration-150"
              />
            </div>
            <div>
              <label className="font-mono text-xs text-sv-secondary uppercase flex items-center gap-1.5">
                <Clock size={13} weight="bold" /> End
              </label>
              <input
                data-testid="window-end-input"
                type="time"
                value={win.window_end}
                onChange={(e) => setWin({ ...win, window_end: e.target.value })}
                className="mt-1 h-11 bg-sv-bg border border-sv-border px-3 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-sv-ink focus:border-sv-ink transition-colors duration-150"
              />
            </div>
            <p className="font-mono text-[11px] text-sv-muted sm:text-right">
              Leave times empty for 24-hour sending.
              <br />
              Overnight windows (e.g. 20:00→06:00) are supported.
            </p>
          </div>

          <div>
            <label className="font-mono text-xs text-sv-secondary uppercase flex items-center gap-1.5 mb-2">
              <CalendarBlank size={13} weight="bold" /> Allowed days
            </label>
            <div className="flex flex-wrap gap-2" data-testid="window-days">
              {DAYS.map(([label, d]) => {
                const on = win.active_days.includes(d);
                return (
                  <button
                    key={d}
                    data-testid={`day-toggle-${d}`}
                    onClick={() => toggleDay(d)}
                    className={`w-14 h-9 font-mono text-xs uppercase border transition-colors duration-150 ${
                      on
                        ? "bg-sv-live/15 border-sv-live text-sv-live"
                        : "bg-sv-bg border-sv-border text-sv-muted hover:text-sv-ink"
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          <button
            data-testid="save-window-button"
            onClick={saveWindow}
            disabled={savingWin}
            className="flex items-center gap-2 h-10 px-5 bg-sv-ink text-sv-onink font-semibold text-sm hover:bg-sv-secondary transition-colors duration-150 disabled:opacity-50"
          >
            <Check size={16} weight="bold" />
            {savingWin ? "Saving…" : "Save send window"}
          </button>
        </div>
      </div>

      {/* Message Sequence */}
      <div className="mt-8">
        <p className="font-mono text-xs text-sv-muted uppercase tracking-widest mb-3">
          // message sequence · timed follow-ups after message 1
        </p>
        <div className="border border-sv-border bg-sv-surface p-5 space-y-4" data-testid="sequence-config">
          <p className="font-mono text-[11px] text-sv-muted leading-relaxed">
            Message 1 is sent first. Enable any follow-up to auto-send it after a wait, timed from when that
            contact's previous message was actually sent. Contacts missing that message column are skipped.
            Follow-ups obey the same rate limit and send window.
          </p>
          {[2, 3, 4, 5].map((m) => {
            const st = seq[String(m)] || { enabled: false, wait_value: 30, wait_unit: "minutes" };
            return (
              <div
                key={m}
                data-testid={`seq-step-${m}`}
                className="flex flex-wrap items-center gap-3 border border-sv-border bg-sv-bg p-3"
              >
                <label className="flex items-center gap-2 min-w-[150px] cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid={`seq-enable-${m}`}
                    checked={st.enabled}
                    onChange={(e) => setStep(String(m), { enabled: e.target.checked })}
                    className="align-middle cursor-pointer accent-black"
                  />
                  <span className="font-mono text-sm uppercase tracking-wide">Message {m}</span>
                </label>
                <div className={`flex items-center gap-2 ${st.enabled ? "" : "opacity-40 pointer-events-none"}`}>
                  <span className="font-mono text-[11px] text-sv-muted uppercase">Wait</span>
                  <input
                    type="number"
                    min="1"
                    max="999"
                    data-testid={`seq-wait-${m}`}
                    value={st.wait_value}
                    onChange={(e) => setStep(String(m), { wait_value: Number(e.target.value) })}
                    className="w-20 h-9 bg-sv-surface border border-sv-border px-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-sv-ink"
                  />
                  <select
                    data-testid={`seq-unit-${m}`}
                    value={st.wait_unit}
                    onChange={(e) => setStep(String(m), { wait_unit: e.target.value })}
                    className="h-9 bg-sv-surface border border-sv-border px-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-sv-ink"
                  >
                    {WAIT_UNITS.map(([l, v]) => (
                      <option key={v} value={v}>{l}</option>
                    ))}
                  </select>
                  <span className="font-mono text-[11px] text-sv-muted">after Message {m - 1} is sent</span>
                </div>
              </div>
            );
          })}
          <button
            data-testid="save-sequence-button"
            onClick={saveSequence}
            disabled={savingSeq}
            className="flex items-center gap-2 h-10 px-5 bg-sv-ink text-sv-onink font-semibold text-sm hover:bg-sv-secondary transition-colors duration-150 disabled:opacity-50"
          >
            <Check size={16} weight="bold" /> {savingSeq ? "Saving…" : "Save sequence"}
          </button>
        </div>
      </div>

      {/* CSV Upload */}
      <div className="mt-8">
        <p className="font-mono text-xs text-sv-muted uppercase tracking-widest mb-3">// contacts</p>
        <div
          data-testid="csv-dropzone"
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); handleFile(e.dataTransfer.files?.[0]); }}
          onClick={() => fileRef.current?.click()}
          className={`border border-dashed p-8 text-center cursor-pointer transition-colors duration-150 ${
            drag ? "border-sv-live bg-sv-live/5" : "border-sv-border bg-sv-surface hover:bg-sv-hover"
          }`}
        >
          <input
            ref={fileRef}
            data-testid="csv-file-input"
            type="file"
            accept=".csv"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
          <UploadSimple size={24} weight="bold" className="mx-auto text-sv-secondary" />
          <p className="font-mono text-sm text-sv-secondary mt-2">
            {uploading ? "UPLOADING…" : "DRAG_AND_DROP_CSV"}
          </p>
          <p className="font-mono text-[11px] text-sv-muted mt-1">
            columns: phone, first_name, company, message_1 … message_5 · duplicates skipped
          </p>
        </div>
      </div>

      {skippedCount > 0 && (
        <div className="mt-3 flex items-center justify-between border border-sv-border bg-sv-surface px-4 py-2">
          <span className="font-mono text-[11px] text-sv-muted uppercase tracking-wider" data-testid="skipped-count">
            {skippedCount} skipped duplicate{skippedCount === 1 ? "" : "s"} for this campaign
          </span>
          <button
            data-testid="download-skipped-button"
            onClick={downloadSkipped}
            className="h-8 px-3 border border-sv-border font-mono text-[11px] uppercase tracking-wider flex items-center gap-2 hover:bg-sv-hover transition-colors duration-150"
          >
            <DownloadSimple size={13} weight="bold" /> Download skipped
          </button>
        </div>
      )}

      {/* Import summary */}
      {lastImport && (
        <div data-testid="import-summary" className="mt-4 border border-sv-border bg-sv-surface p-4">
          <div className="flex items-center justify-between">
            <p className="font-mono text-xs text-sv-muted uppercase tracking-widest">// last import</p>
            <button
              data-testid="dismiss-import-summary"
              onClick={() => setLastImport(null)}
              className="font-mono text-[11px] text-sv-muted hover:text-sv-ink"
            >
              dismiss
            </button>
          </div>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { k: "added", label: "Added", val: lastImport.added, color: "#16A34A", tid: "import-added" },
              { k: "dupes", label: "Duplicates", val: lastImport.skipped_duplicates, color: "#71717A", tid: "import-duplicates" },
              { k: "blank", label: "Blank rows", val: lastImport.blank, color: "#71717A", tid: "import-blank" },
              { k: "invalid", label: "Invalid", val: lastImport.invalid, color: lastImport.invalid > 0 ? "#DC2626" : "#71717A", tid: "import-invalid" },
            ].map((m) => (
              <div key={m.k} className="border border-sv-border bg-sv-bg p-3">
                <p className="font-mono text-[10px] text-sv-muted uppercase tracking-wider">{m.label}</p>
                <p className="font-mono text-2xl mt-1" style={{ color: m.color }} data-testid={m.tid}>
                  {m.val}
                </p>
              </div>
            ))}
          </div>
          {lastImport.invalid > 0 && (
            <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
              <p className="font-mono text-[11px]" style={{ color: "#DC2626" }} data-testid="import-invalid-samples">
                {lastImport.invalid_samples?.length ? `Invalid examples: ${lastImport.invalid_samples.join(", ")} — ` : ""}
                imported as-is and tagged; they may fail to send.
              </p>
              <button
                data-testid="remove-invalid-button"
                onClick={removeInvalid}
                className="h-9 px-4 border font-mono text-xs uppercase tracking-wider hover:brightness-95 transition-all duration-150 flex items-center gap-2"
                style={{ color: "#DC2626", borderColor: "#DC262655", background: "#DC262612" }}
              >
                <Trash size={14} weight="bold" /> Remove {lastImport.invalid} invalid
              </button>
            </div>
          )}
        </div>
      )}

      {/* Contacts table */}
      <div className="mt-8 flex items-center justify-between">
        <p className="font-mono text-xs text-sv-muted uppercase tracking-widest">// results</p>
        <button
          data-testid="export-csv-button"
          onClick={exportCsv}
          className="flex items-center gap-2 h-9 px-4 border border-sv-border text-sm hover:bg-sv-hover transition-colors duration-150"
        >
          <DownloadSimple size={15} weight="bold" /> Export CSV
        </button>
      </div>
      {selected.size > 0 && (
        <div
          data-testid="bulk-actions-bar"
          className="mt-3 -mb-2 flex items-center justify-between border border-sv-ink bg-sv-ink text-sv-onink px-4 py-2"
        >
          <span className="font-mono text-xs uppercase tracking-wider" data-testid="bulk-selected-count">
            {selected.size} selected
          </span>
          <div className="flex items-center gap-2">
            <button
              data-testid="bulk-clear-button"
              onClick={() => setSelected(new Set())}
              className="h-8 px-3 font-mono text-[11px] uppercase tracking-wider border border-sv-onink/40 hover:bg-sv-onink/10 transition-colors duration-150"
            >
              Clear
            </button>
            <button
              data-testid="bulk-delete-button"
              onClick={deleteSelected}
              className="h-8 px-3 font-mono text-[11px] uppercase tracking-wider flex items-center gap-1.5"
              style={{ background: "#DC2626", color: "#fff" }}
            >
              <Trash size={13} weight="bold" /> Delete selected
            </button>
          </div>
        </div>
      )}
      <div className="mt-6 border border-sv-border overflow-x-auto">
        <table className="w-full text-sm" data-testid="contacts-table">
          <thead>
            <tr className="bg-sv-surface border-b border-sv-border font-mono text-[11px] text-sv-muted uppercase tracking-wider">
              <th className="text-left px-4 py-3 font-medium w-10">
                <input
                  type="checkbox"
                  data-testid="select-all-contacts"
                  checked={contacts.length > 0 && selected.size === contacts.length}
                  onChange={toggleAll}
                  className="align-middle cursor-pointer accent-black"
                />
              </th>
              <th className="text-left px-4 py-3 font-medium">Phone</th>
              <th className="text-left px-4 py-3 font-medium">message_1</th>
              <th className="text-left px-4 py-3 font-medium">Status</th>
              <th className="text-left px-4 py-3 font-medium">Step</th>
              <th className="text-left px-4 py-3 font-medium">Next scheduled</th>
              <th className="text-left px-4 py-3 font-medium">Delivery</th>
              <th className="text-left px-4 py-3 font-medium">Message ID</th>
              <th className="text-left px-4 py-3 font-medium">Att.</th>
            </tr>
          </thead>
          <tbody>
            {contacts.length === 0 ? (
              <tr>
                <td colSpan="9" className="px-4 py-10 text-center font-mono text-sm text-sv-muted">
                  NO_CONTACTS — upload a CSV to begin
                </td>
              </tr>
            ) : (
              contacts.map((c) => (
                <tr
                  key={c.id}
                  data-testid={`contact-row-${c.id}`}
                  className={`border-b border-sv-border last:border-0 hover:bg-sv-hover transition-colors duration-150 ${
                    selected.has(c.id) ? "bg-sv-hover" : ""
                  }`}
                >
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      data-testid={`contact-checkbox-${c.id}`}
                      checked={selected.has(c.id)}
                      onChange={() => toggleOne(c.id)}
                      className="align-middle cursor-pointer accent-black"
                    />
                  </td>
                  <td className="px-4 py-3 font-mono text-sv-ink whitespace-nowrap" data-testid={`contact-phone-${c.id}`}>
                    <span className="flex items-center gap-2">
                      {c.phone}
                      {c.valid === false && (
                        <span
                          data-testid={`contact-invalid-tag-${c.id}`}
                          className="px-1.5 py-0.5 border text-[10px] uppercase tracking-wider"
                          style={{ color: "#DC2626", borderColor: "#DC262655", background: "#DC262612" }}
                        >
                          invalid
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sv-secondary max-w-[320px] truncate">{c.message_1 || "—"}</td>
                  <td className="px-4 py-3">
                    <StatusBadge status={c.status} />
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-sv-muted" data-testid={`contact-step-${c.id}`}>
                    {(c.last_step_sent || 0)}/{seqCount}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs" data-testid={`contact-next-${c.id}`}>
                    {c.status === "scheduled" && c.next_step_at ? (
                      <span style={{ color: "#2563EB" }}>{fmtSched(c.next_step_at)}</span>
                    ) : (
                      <span className="text-sv-muted">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3" data-testid={`contact-delivery-${c.id}`}>
                    {c.delivery_status ? (
                      <StatusBadge status={c.delivery_status} />
                    ) : (
                      <span className="font-mono text-xs text-sv-muted">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs" data-testid={`contact-msgid-${c.id}`}>
                    {c.sendivo_message_id ? (
                      <span className="flex items-center gap-2">
                        {String(c.sendivo_message_id).startsWith("mock_") && (
                          <span
                            data-testid={`contact-mock-tag-${c.id}`}
                            className="px-1.5 py-0.5 border text-[10px] uppercase tracking-wider"
                            style={{ color: "#B45309", borderColor: "#B4530955", background: "#B4530912" }}
                          >
                            mock
                          </span>
                        )}
                        <span className="text-sv-live">{c.sendivo_message_id}</span>
                      </span>
                    ) : c.error ? (
                      <span className="text-sv-stop">{c.error}</span>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-sv-muted">{c.attempts || 0}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {dupData && (
        <div
          data-testid="duplicate-warning-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
        >
          <div className="w-full max-w-2xl border border-sv-border bg-sv-surface flex flex-col max-h-[85vh]">
            <div className="px-6 py-4 border-b border-sv-border">
              <p className="font-mono text-xs text-sv-muted uppercase tracking-widest">// duplicate check</p>
              <h3 className="font-display font-black text-2xl tracking-tight uppercase mt-1">
                {dupData.duplicate_count} of {dupData.total} already contacted
              </h3>
              <p className="text-sv-secondary text-sm mt-1">
                These numbers appear in your historical sent messages (across all campaigns and sending numbers).
              </p>
            </div>

            {!dupReview ? (
              <>
                <div className="flex-1 overflow-y-auto px-6 py-3" data-testid="duplicate-list">
                  {dupData.duplicates.map((d) => (
                    <div key={d.contact_id} className="flex items-center justify-between py-2 border-b border-sv-border last:border-0">
                      <div>
                        <span className="font-mono text-sm text-sv-ink">{d.phone}</span>
                        {(d.first_name || d.company) && (
                          <span className="text-sv-muted text-sm ml-2">{[d.first_name, d.company].filter(Boolean).join(" · ")}</span>
                        )}
                      </div>
                      <span className="font-mono text-[11px] text-sv-muted">
                        last: {d.last_contacted_at ? new Date(d.last_contacted_at).toLocaleDateString() : "—"} · {d.times_contacted}×
                      </span>
                    </div>
                  ))}
                </div>
                <div className="px-6 py-4 border-t border-sv-border flex flex-wrap gap-2 justify-end">
                  <button
                    data-testid="dup-include-all"
                    disabled={dupBusy}
                    onClick={() => applySkip([])}
                    className="h-10 px-4 border border-sv-border text-sm hover:bg-sv-hover transition-colors duration-150 disabled:opacity-50"
                  >
                    Include all anyway
                  </button>
                  <button
                    data-testid="dup-review"
                    disabled={dupBusy}
                    onClick={() => { setReviewIdx(0); setReviewSkip(new Set()); setDupReview(true); }}
                    className="h-10 px-4 border border-sv-ink text-sm hover:bg-sv-hover transition-colors duration-150 disabled:opacity-50"
                  >
                    Review one at a time
                  </button>
                  <button
                    data-testid="dup-skip-all"
                    disabled={dupBusy}
                    onClick={() => applySkip(dupData.duplicates.map((d) => d.contact_id))}
                    className="h-10 px-5 font-semibold text-sm text-white transition-all duration-150 disabled:opacity-50"
                    style={{ background: "#DC2626" }}
                  >
                    {dupBusy ? "…" : "Skip all duplicates"}
                  </button>
                </div>
              </>
            ) : (
              <>
                {(() => {
                  const d = dupData.duplicates[reviewIdx];
                  return (
                    <div className="flex-1 px-6 py-6" data-testid="duplicate-review">
                      <p className="font-mono text-[11px] text-sv-muted uppercase tracking-wider">
                        Reviewing {reviewIdx + 1} of {dupData.duplicates.length}
                      </p>
                      <p className="font-mono text-2xl text-sv-ink mt-2">{d.phone}</p>
                      {(d.first_name || d.company) && (
                        <p className="text-sv-secondary text-sm mt-1">{[d.first_name, d.last_name, d.company].filter(Boolean).join(" · ")}</p>
                      )}
                      <p className="font-mono text-sm text-sv-muted mt-3">
                        Last contacted: {d.last_contacted_at ? new Date(d.last_contacted_at).toLocaleString() : "—"} · {d.times_contacted} time(s)
                      </p>
                    </div>
                  );
                })()}
                <div className="px-6 py-4 border-t border-sv-border flex gap-2 justify-end">
                  <button
                    data-testid="review-keep"
                    disabled={dupBusy}
                    onClick={() => {
                      const next = reviewIdx + 1;
                      if (next >= dupData.duplicates.length) applySkip(Array.from(reviewSkip));
                      else setReviewIdx(next);
                    }}
                    className="h-10 px-5 border border-sv-border text-sm hover:bg-sv-hover transition-colors duration-150 disabled:opacity-50"
                  >
                    Keep
                  </button>
                  <button
                    data-testid="review-skip"
                    disabled={dupBusy}
                    onClick={() => {
                      const id2 = dupData.duplicates[reviewIdx].contact_id;
                      const s = new Set(reviewSkip); s.add(id2); setReviewSkip(s);
                      const next = reviewIdx + 1;
                      if (next >= dupData.duplicates.length) applySkip(Array.from(s));
                      else setReviewIdx(next);
                    }}
                    className="h-10 px-5 font-semibold text-sm text-white transition-all duration-150 disabled:opacity-50"
                    style={{ background: "#DC2626" }}
                  >
                    Skip this one
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
}
